// State of the thread actions started from the inbox views, shared by the list and the reader:
// the label picker that is open, the work each action queued (followed until Gmail confirmed it,
// with its undo), and the rows hidden locally while their action runs (the optimistic hide with
// an undo toast; a `thread` sync event brings the real list back). Thread actions are reversible,
// so they run on one press and nothing here is ever armed. A module store, so navigating between
// the list and a thread keeps it.
import { useSyncExternalStore } from "react";
import type { ThreadActionRequest, ThreadSummary } from "@shared/api";
import type { ListSearch } from "../listSearch";

export interface FollowedAction {
  workId: string;
  threadKey: string;
  thread: ThreadSummary;
  request: ThreadActionRequest;
  reverse: ThreadActionRequest;
  /** The list it was started from: its reverse hides or shows the row there the same way. */
  search: ListSearch;
  startedAt: number;
  /** The status the follow already told the reader about (one toast per transition). */
  announced: string | null;
  /** The "Archiving…" toast with Undo, dropped once the work settles. */
  undoToastId: string | null;
  /** The reader closed the panel (the work still finishes; the toast still offers Undo). */
  dismissed: boolean;
}

interface State {
  /** The thread whose label picker is open. */
  picker: ThreadSummary | null;
  /** The newest followed action per thread key. */
  follows: ReadonlyMap<string, FollowedAction>;
  /** Thread keys hidden locally, with the work that hides them. */
  hidden: ReadonlyMap<string, string>;
  /** Thread keys the reader asked back into a list: shown as soon as they are fetched. */
  revealed: ReadonlySet<string>;
}

let state: State = {
  picker: null,
  follows: new Map(),
  hidden: new Map(),
  revealed: new Set(),
};
const listeners = new Set<() => void>();

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function threadActionState(): State {
  return state;
}

export function useThreadActionState(): State {
  return useSyncExternalStore(subscribe, threadActionState, threadActionState);
}

export function openLabelPicker(thread: ThreadSummary): void {
  set({ picker: thread });
}

export function closeLabelPicker(): void {
  if (state.picker) set({ picker: null });
}

export function followThreadAction(follow: FollowedAction): void {
  const follows = new Map(state.follows);
  follows.set(follow.threadKey, follow);
  set({ follows });
}

export function patchFollow(workId: string, patch: Partial<FollowedAction>): void {
  const current = [...state.follows.values()].find((f) => f.workId === workId);
  if (!current) return;
  const follows = new Map(state.follows);
  follows.set(current.threadKey, { ...current, ...patch });
  set({ follows });
}

export function dismissFollow(workId: string): void {
  patchFollow(workId, { dismissed: true });
}

export function followFor(threadKey: string): FollowedAction | null {
  return state.follows.get(threadKey) ?? null;
}

export function hideThread(threadKey: string, workId: string): void {
  const hidden = new Map(state.hidden);
  hidden.set(threadKey, workId);
  set({ hidden });
}

/**
 * Show the row again (an undo, a cancel, or work that did not run): the key leaves `hidden`
 * and joins `revealed`, so the stable list takes it back at once instead of holding it behind
 * the "new arrivals" pill.
 */
export function unhideThread(threadKey: string, workId?: string): void {
  if (!state.hidden.has(threadKey)) return;
  if (workId && state.hidden.get(threadKey) !== workId) return;
  const hidden = new Map(state.hidden);
  hidden.delete(threadKey);
  set({ hidden, revealed: new Set([...state.revealed, threadKey]) });
}

export function revealThread(threadKey: string): void {
  if (state.revealed.has(threadKey)) return;
  set({ revealed: new Set([...state.revealed, threadKey]) });
}

export function unrevealThread(threadKey: string): void {
  if (!state.revealed.has(threadKey)) return;
  const revealed = new Set(state.revealed);
  revealed.delete(threadKey);
  set({ revealed });
}

/** Test seam. */
export function resetThreadActionState(): void {
  set({ picker: null, follows: new Map(), hidden: new Map(), revealed: new Set() });
}
